import { useState, useRef, useCallback, useEffect } from 'react';

const MAX_DURATION_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Why a recording ended. Passed to onRecordingComplete and exposed as
 * `endReason`, so whoever shows the result can say what happened: a recording
 * that ends without Stop being pressed otherwise looks like it "just stopped".
 */
export const END_REASONS = {
    USER: 'user',               // Stop was pressed
    LIMIT: 'limit',             // the maximum length was reached
    SHARE_ENDED: 'share-ended', // the browser ended the screen share: "Stop sharing", or the shared window or tab was closed
    ERROR: 'error',             // the browser's recorder failed
    CLOSED: 'closed'            // the recorder itself was removed (leaving the page)
};

/** One line for a recording that ended on its own; null when the user stopped it. */
export const endReasonText = (reason, { empty = false } = {}) => {
    switch (reason) {
        case END_REASONS.LIMIT:
            return `Recording stopped at the ${MAX_DURATION_MS / 60000}-minute limit.`;
        case END_REASONS.SHARE_ENDED:
            return 'Recording stopped because screen sharing ended (the shared window or tab was closed, or sharing was stopped).';
        case END_REASONS.ERROR:
            return empty
                ? 'Recording failed because of a browser error, and nothing was captured.'
                : 'Recording stopped because of a browser error. Check it before sending.';
        default:
            return null;
    }
};

/**
 * A finished take as an uploadable File with a recognizable name.
 *
 * Safari cannot record WebM and produces an MP4, so hardcoding .webm shipped
 * an MP4 under a WebM name and mime type — which neither the browser nor
 * QuickTime will open. Both are taken from the blob the recorder produced.
 */
export function recordingToFile(blob) {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const mime = (blob.type || 'video/webm').split(';')[0];
    const ext = mime === 'video/mp4' ? 'mp4' : 'webm';
    const filename = `screen-recording-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}.${ext}`;
    return new File([blob], filename, { type: mime });
}

export function useScreenRecorder({ onRecordingComplete, maxDurationMs = MAX_DURATION_MS } = {}) {
    const [status, setStatus] = useState('idle'); // idle, requesting, recording, paused, preview
    const [recordingTimeMs, setRecordingTimeMs] = useState(0);
    const [micLevel, setMicLevel] = useState(0);
    const [micAvailable, setMicAvailable] = useState(false);
    const [micWaveform, setMicWaveform] = useState(() => Array(18).fill(0));
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [endReason, setEndReason] = useState(null);

    const onRecordingCompleteRef = useRef(onRecordingComplete);
    useEffect(() => {
        onRecordingCompleteRef.current = onRecordingComplete;
    }, [onRecordingComplete]);

    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const sourceStreamsRef = useRef([]);
    const chunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    const lastMicSampleRef = useRef(0);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const pauseTimeRef = useRef(null);
    // Set by whatever ends the take, read when the recorder reports it stopped.
    const endReasonRef = useRef(null);

    const getSupportedMimeType = () => {
        // VP8 first, deliberately. This file is an intermediate — the nightly
        // job re-encodes it to H.264 — so encode *speed* matters far more than
        // compression ratio, and VP8 software-encodes several times faster than
        // VP9. That is the difference between a Mac keeping real time and
        // lagging minutes behind the recording.
        const types = [
            'video/webm;codecs=vp8,opus',
            'video/webm;codecs=vp9,opus',
            'video/webm',
            'video/mp4' // Safari, which cannot record WebM at all
        ];
        for (const type of types) {
            if (window.MediaRecorder && MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }
        return '';
    };

    const cleanup = useCallback(() => {
        sourceStreamsRef.current.forEach(stream => stream.getTracks().forEach(track => track.stop()));
        sourceStreamsRef.current = [];
        analyserRef.current = null;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            try { mediaRecorderRef.current.stop(); } catch (e) {}
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close().catch(() => {});
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
    }, []);

    const updateMicLevel = useCallback(() => {
        if (!analyserRef.current) return;
        animationFrameRef.current = requestAnimationFrame(updateMicLevel);
        const now = Date.now();
        if (now - lastMicSampleRef.current < 80) return;
        lastMicSampleRef.current = now;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
            sum += ((dataArray[i] - 128) / 128) ** 2;
        }
        const level = Math.min(1, Math.sqrt(sum / dataArray.length) * 5);
        setMicLevel(level);
        if (mediaRecorderRef.current?.state === 'recording') {
            setMicWaveform(previous => [...previous.slice(1), level]);
        }
    }, []);

    // Every ending goes through here with its reason. The first reason wins: a
    // failing recorder that is then stopped is still a failure.
    const endRecording = useCallback((reason) => {
        if (!endReasonRef.current) endReasonRef.current = reason;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        } else if (status === 'recording') {
            setStatus('idle');
            cleanup();
        }
        window.onbeforeunload = null;
    }, [status, cleanup]);

    // The Stop button. Takes no arguments on purpose: it is used directly as a
    // click handler, and the click event must not be mistaken for a reason.
    const stopRecording = useCallback(() => endRecording(END_REASONS.USER), [endRecording]);

    const updateTimer = useCallback(() => {
        if (status !== 'recording') return;
        const now = Date.now();
        const elapsed = now - startTimeRef.current;
        setRecordingTimeMs(elapsed);
        if (elapsed >= maxDurationMs) {
            endRecording(END_REASONS.LIMIT);
        }
    }, [status, maxDurationMs, endRecording]);

    useEffect(() => {
        if (status === 'recording') {
            timerRef.current = setInterval(updateTimer, 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [status, updateTimer]);

    const startRecording = async () => {
        try {
            setStatus('requesting');
            setMicAvailable(false);
            setMicWaveform(Array(18).fill(0));
            setRecordedBlob(null);
            setEndReason(null);
            endReasonRef.current = null;
            chunksRef.current = [];

            // 1. Get Screen Stream
            let displayStream;
            try {
                displayStream = await navigator.mediaDevices.getDisplayMedia({
                    // Uncapped, this captures the display's *native* resolution.
                    // On a Retina MacBook that is 3456x2234 — nearly 4x the
                    // pixels of a 1080p Windows laptop — and no Mac has a
                    // hardware VP9 encoder, so the software encoder falls
                    // behind and stopping the recording stalls for a long time
                    // while it drains. 1080p is plenty for a screen share, and
                    // the midnight job re-encodes to H.264 anyway.
                    video: {
                        displaySurface: 'monitor',
                        width: { max: 1920 },
                        height: { max: 1080 },
                        frameRate: { ideal: 15, max: 24 }
                    },
                    audio: true // try to get system audio if available
                });
            } catch (err) {
                // User cancelled or not supported
                setStatus('idle');
                return;
            }

            sourceStreamsRef.current.push(displayStream);
            // Listen for native stop (e.g., Chrome's "Stop sharing" bar)
            displayStream.getVideoTracks()[0].onended = () => {
                endRecording(END_REASONS.SHARE_ENDED);
            };

            // 2. Get Mic Stream
            let micStream = null;
            try {
                micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                sourceStreamsRef.current.push(micStream);
                setMicAvailable(micStream.getAudioTracks().some(track => track.readyState === 'live'));
                micStream.getAudioTracks().forEach(track => {
                    track.onended = () => setMicAvailable(false);
                });
            } catch (err) {
                console.warn('Microphone permission denied or not available. Recording without narration.', err);
            }

            // 3. Mix Audio if needed
            let mixedStream = displayStream;
            let finalTracks = [displayStream.getVideoTracks()[0]];

            if (micStream || displayStream.getAudioTracks().length > 0) {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                const audioCtx = new AudioContext();
                audioContextRef.current = audioCtx;
                if (audioCtx.state === 'suspended') await audioCtx.resume();
                const dest = audioCtx.createMediaStreamDestination();

                if (micStream && micStream.getAudioTracks().length > 0) {
                    const micSource = audioCtx.createMediaStreamSource(micStream);
                    micSource.connect(dest);
                    
                    // Setup mic level analyser
                    const analyser = audioCtx.createAnalyser();
                    analyser.fftSize = 256;
                    micSource.connect(analyser);
                    analyserRef.current = analyser;
                    updateMicLevel();
                }

                if (displayStream.getAudioTracks().length > 0) {
                    const displayAudioSource = audioCtx.createMediaStreamSource(displayStream);
                    displayAudioSource.connect(dest);
                }

                if (dest.stream.getAudioTracks().length > 0) {
                     finalTracks.push(dest.stream.getAudioTracks()[0]);
                }
            }

            mixedStream = new MediaStream(finalTracks);
            streamRef.current = mixedStream;

            // 4. Setup MediaRecorder
            const mimeType = getSupportedMimeType();
            const options = { videoBitsPerSecond: 1500000 }; // 1.5 Mbps
            if (mimeType) {
                options.mimeType = mimeType;
            }
            const mediaRecorder = new MediaRecorder(mixedStream, options);
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            // The browser's recorder gave up (its encoder failed, or the stream
            // changed under it). It stops by itself after this and hands over
            // what it has, which `onstop` below files; all this does is make sure
            // the take is reported as a failure and not as a finished recording.
            // The explicit stop covers a browser that reports the error and then
            // leaves the recorder running.
            mediaRecorder.onerror = (event) => {
                console.error('Screen recorder error:', event?.error || event);
                endReasonRef.current = END_REASONS.ERROR;
                if (mediaRecorder.state !== 'inactive') {
                    try { mediaRecorder.stop(); } catch (e) { /* already stopping */ }
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType || 'video/webm' });
                // A stop nobody asked for and no error event explained: the
                // recorder stopped on its own, so it is reported as an error
                // rather than passed off as the user's doing.
                const reason = endReasonRef.current || END_REASONS.ERROR;
                // Cleared here as well as in endRecording: a recorder that stops
                // or fails by itself never goes through that, and the leave-page
                // warning must not outlive the recording.
                window.onbeforeunload = null;
                setRecordedBlob(blob);
                setEndReason(reason);
                setStatus('preview');
                
                if (onRecordingCompleteRef.current) {
                    onRecordingCompleteRef.current(blob, { reason });
                }
                
                try {
                    cleanup();
                } catch (err) {
                    console.error('Screen recorder cleanup error:', err);
                }
            };

            mediaRecorder.start(1000); // 1-second chunks
            startTimeRef.current = Date.now();
            setRecordingTimeMs(0);
            setStatus('recording');

            // Handle page unload warning
            window.onbeforeunload = () => "A recording is in progress. Are you sure you want to leave?";

        } catch (err) {
            console.error('Failed to start recording:', err);
            setStatus('idle');
            cleanup();
        }
    };

    const pauseRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.pause();
            pauseTimeRef.current = Date.now();
            setStatus('paused');
        }
    };

    const resumeRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
            mediaRecorderRef.current.resume();
            if (pauseTimeRef.current) {
                 // adjust start time by paused duration
                 startTimeRef.current += (Date.now() - pauseTimeRef.current);
            }
            setStatus('recording');
        }
    };

    const discardRecording = () => {
        setRecordedBlob(null);
        setEndReason(null);
        endReasonRef.current = null;
        chunksRef.current = [];
        setStatus('idle');
        setRecordingTimeMs(0);
        window.onbeforeunload = null;
    };

    // Unmounting mid-recording stops it here, not through stopRecording, so the
    // "recording in progress" leave-page warning has to be cleared here too —
    // otherwise it outlives the recording and nags on every refresh.
    useEffect(() => {
        return () => {
            if (!endReasonRef.current) endReasonRef.current = END_REASONS.CLOSED;
            cleanup();
            window.onbeforeunload = null;
        };
    }, [cleanup]);

    const isSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia && window.MediaRecorder);

    return {
        isSupported,
        status, // idle, requesting, recording, paused, preview
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        discardRecording,
        recordingTimeMs,
        micLevel,
        micAvailable,
        micWaveform,
        recordedBlob,
        endReason, // why the last take ended (END_REASONS); null while recording or idle
        stream: status === 'recording' || status === 'paused' ? streamRef.current : null
    };
}
